import { catalogApiRef, CatalogApi } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { RelationshipsCard } from './RelationshipsCard';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getEntityRefs } from './common';
import { RELATION_PART_OF } from '@backstage/catalog-model';

/**
 * Find all the systems that are related to the passed entities list
 */
const entitiesToSystems = async (
  currentSystem: Entity,
  entities: Entity[],
  catalogApi: CatalogApi,
): Promise<Entity[]> => {
  const refs = getEntityRefs(entities, RELATION_PART_OF);
  const uniqRefs = [...new Set(refs)] as string[];
  const uniqRefsWithoutCurrentSystemRef = uniqRefs
    .filter(ref => !ref.includes(currentSystem.metadata.name))
    .sort();
  const relatedSystems = await catalogApi.getEntitiesByRefs({
    entityRefs: uniqRefsWithoutCurrentSystemRef,
  });

  return relatedSystems?.items as Entity[];
};

/**
 * To render system relationships we need to convert
 * the usedBy and using entities list to usedBy and using systems list
 */
export const SystemRelationshipsWrapperCard = ({
  usedByEntities,
  usingEntities,
}: {
  usedByEntities: Entity[];
  usingEntities: Entity[];
}) => {
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();

  /**
   * Find the unique list of all the systems that are used by entities:
   * 1. that are using current entity
   * 2. current entity is using
   */
  const { value } = useAsync(async () => {
    let usedBySystems = await entitiesToSystems(
      entity,
      usedByEntities,
      catalogApi,
    );
    let usingSystems = await entitiesToSystems(
      entity,
      usingEntities,
      catalogApi,
    );

    usedBySystems = usedBySystems.filter(ent => ent);
    usingSystems = usingSystems.filter(ent => ent);

    return {
      usedBySystems,
      usingSystems,
    };
  }, [usedByEntities, usingEntities]);

  /* Nothing to render, why waste it */
  if (!value) return null;
  return (
    <RelationshipsCard
      usedBy={value.usedBySystems}
      using={value.usingSystems}
    />
  );
};
